import { useInfiniteQuery } from '@tanstack/react-query'
import { Empty, Flex, Spin } from 'antd'
import { SelectProps } from 'antd/lib'
import { useState } from 'react'

import { useInstitutionServiceGetInstitutionListEndpointKey } from 'src/api/queries'
import {
  GetInstitutionListResponse,
  GetInstitutionListResponseItem,
  InstitutionService
} from 'src/api/requests'
import { DEFAULT_PER_PAGE } from 'src/app-constants'
import { Select } from 'src/components'
import { useDebounce } from 'src/hooks'
import { useLocales } from 'src/locales'

type Props = SelectProps & {
  onItemSelect?: (item: GetInstitutionListResponseItem | undefined) => void
}

export const InstitutionsSelect = ({ onItemSelect, ...props }: Props) => {
  const { t } = useLocales()
  const [search, setSearch] = useState('')
  const debouncedSearch = useDebounce(search)

  const { data, fetchNextPage, isFetching } = useInfiniteQuery({
    queryKey: [
      useInstitutionServiceGetInstitutionListEndpointKey,
      debouncedSearch
    ],
    queryFn: async ({ pageParam = 1 }: { pageParam?: number }) => {
      const response = await InstitutionService.getInstitutionListEndpoint(
        pageParam,
        DEFAULT_PER_PAGE,
        undefined,
        debouncedSearch
      )

      return {
        data: response?.data ?? [],
        total_pages: response.totalPages ?? 0,
        current_page: pageParam
      }
    },
    getNextPageParam: (lastPage: {
      data: GetInstitutionListResponse
      total_pages: number
      current_page: number
    }) =>
      lastPage?.current_page < lastPage?.total_pages
        ? lastPage.current_page + 1
        : undefined,
    initialPageParam: 1
  })

  const items = data?.pages.flatMap((page) => page.data ?? []) ?? []

  const options = items.map((item: GetInstitutionListResponseItem) => ({
    value: item.id,
    label: item?.name
  }))

  return (
    <Select
      allowClear
      popupMatchSelectWidth={true}
      filterOption={false}
      options={options}
      onSearch={(value) => setSearch(value)}
      loading={isFetching}
      placeholder={t('common.institution')}
      showSearch={true}
      onPopupScroll={(e) => {
        const target = e.target as HTMLElement
        if (target.scrollTop + target.offsetHeight === target.scrollHeight)
          fetchNextPage()
      }}
      notFoundContent={
        isFetching ? (
          <Flex justify="center">
            <Spin />
          </Flex>
        ) : (
          <Empty />
        )
      }
      {...props}
      onClear={() => {
        setSearch('')
        onItemSelect?.(undefined)
        props.onClear?.()
      }}
      onChange={(value, option) => {
        const found = items.find((item) => item.id === value)
        onItemSelect?.(found)
        props.onChange?.(value, option)
      }}
    />
  )
}
